import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { resolveLogoSrc } from "../utils/resolveLogoSrc";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  IconButton,
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PeopleIcon from "@mui/icons-material/People";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ReusableDataGrid from "../Components/ReusableDataGrid";
import { isIncludedGroupServicePart } from "../utils/receiptCalculations";

function DailyReports() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const [receipts, setReceipts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicle] = useState([]);
  const [servicesCatalog, setServicesCatalog] = useState([]);
  const [dealersSale, setDealersSale] = useState(0);
  const [dealersCount, setDealersCount] = useState(0);
  const [discountCount, setDiscountCount] = useState(0);
  const [taxCount, setTaxCount] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [netSale, setNetSale] = useState(0);
  const [paidToday, setPaidToday] = useState(0);
  const [cardPayment, setCardPayment] = useState(0);
  const [cashPayment, setCashPayment] = useState(0);
  const [checkPayment, setCheckPayment] = useState(0);
  const [todayRemaining, setTodayRemaining] = useState(0);
  const [totalRemaining, setTotalRemaining] = useState(0);
  const [allReceipts, setAllReceipts] = useState([]);
  const [loading, setLoading] = useState(true);

  const isInvoiceReceipt = (receipt) =>
    String(receipt?.receiptType || "").toLowerCase() === "invoice";

  const toLocalDateString = (value) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [selectedDate, setSelectedDate] = useState(() =>
    toLocalDateString(new Date()),
  );

  const formatSelectedDateLabel = (value) => {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return "";
    const [year, month, day] = value
      .split("-")
      .map((part) => parseInt(part, 10));
    const localDate = new Date(year, month - 1, day);
    return localDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const selectedDateLabel = formatSelectedDateLabel(selectedDate);

  // Get shop logo
  const shopLogoSrc =
    resolveLogoSrc(Helpers.authShop?.logo) ||
    require("../images/logo-light.png");

  const handleDateChange = (event) => {
    const value = event.target.value;
    setSelectedDate(value);
  };

  const calcPrice = (item) => {
    let total = 0;
    for (let index = 0; index < item.length; index++) {
      total += item[index].totalPrice;
    }
    return total.toFixed(2);
  };

  const formatCurrency = (value) => Number(value || 0).toFixed(2);

  useEffect(() => {
    setLoading(true);
    const tzOffsetMinutes = new Date().getTimezoneOffset();
    const dateQuery = selectedDate
      ? `?date=${encodeURIComponent(selectedDate)}&tzOffsetMinutes=${tzOffsetMinutes}`
      : `?tzOffsetMinutes=${tzOffsetMinutes}`;
    axios
      .get(
        `${Helpers.baseUrl}/reports/daily/${Helpers.authParentId}${dateQuery}`,
        Helpers.headers,
      )
      .then((response) => {
        const invoiceReceipts = (response.data.receipts || []).filter(
          isInvoiceReceipt,
        );
        const paymentSummary = response.data.paymentSummary || {};
        setReceipts(invoiceReceipts.reverse());
        setCustomers(response.data.customers.reverse());
        setVehicle(response.data.vehicles.reverse());
        setPaidToday(Number(paymentSummary.total) || 0);
        setCashPayment(Number(paymentSummary.cash) || 0);
        setCardPayment(Number(paymentSummary.card) || 0);
        setCheckPayment(Number(paymentSummary.check) || 0);
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate, selectedDate]);

  useEffect(() => {
    axios
      .get(
        `${Helpers.baseUrl}/receipts/all/${Helpers.authParentId}`,
        Helpers.headers,
      )
      .then((response) => {
        setAllReceipts(response.data.reverse());
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
      });
  }, [navigate]);

  useEffect(() => {
    axios
      .get(
        `${Helpers.baseUrl}/services/all/${Helpers.authParentId}`,
        Helpers.headers,
      )
      .then((response) => {
        setServicesCatalog(Array.isArray(response.data) ? response.data : []);
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
      });
  }, [navigate]);

  useEffect(() => {
    const sum = allReceipts
      .filter((receipt) => {
        const type = String(receipt?.receiptType || "").toLowerCase();
        return type === "invoice" && receipt?.status !== "Paid";
      })
      .reduce((acc, receipt) => acc + (Number(receipt.remaining) || 0), 0);
    setTotalRemaining(sum);
  }, [allReceipts]);

  useEffect(() => {
    const nonTaxable = receipts.filter(
      (receipt) => receipt.customer?.tax === "Non Taxable",
    );
    setDealersSale(
      nonTaxable.reduce((acc, receipt) => acc + receipt.totalPrice, 0),
    );
    setDealersCount(nonTaxable.length);

    const discounted = receipts.filter((receipt) => Number(receipt.discount) > 0);
    setTotalDiscount(
      discounted.reduce(
        (acc, receipt) =>
          acc + Number(receipt.discountAmount ?? receipt.discount ?? 0),
        0,
      ),
    );
    setDiscountCount(discounted.length);

    const taxed = receipts.filter((receipt) => Number(receipt.tax) > 0);
    setTotalTax(
      taxed.reduce((acc, receipt) => acc + Number(receipt.tax || 0), 0),
    );
    setTaxCount(taxed.length);

    setNetSale(
      receipts.reduce((acc, receipt) => acc + (Number(receipt.totalPrice) || 0), 0),
    );

    const notPaidReceipts = receipts.filter((receipt) => receipt.status !== "Paid");
    setTodayRemaining(
      notPaidReceipts.reduce(
        (acc, receipt) => acc + (Number(receipt.remaining) || 0),
        0,
      ),
    );
  }, [receipts]);

  const lineSummaryRows = useMemo(() => {
    const sectionMaps = {
      service: new Map(),
      part: new Map(),
      oil: new Map(),
      tire: new Map(),
    };
    const normalizeName = (value) =>
      String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
    const getSortPriority = (value) => {
      const priority = Number(value);
      return Number.isFinite(priority) && priority > 0
        ? priority
        : Number.MAX_SAFE_INTEGER;
    };
    const servicePriorityById = new Map();
    const servicePriorityByName = new Map();

    servicesCatalog.forEach((service) => {
      const priority = service?.priority;
      const sortPriority = getSortPriority(priority);
      const id = service?._id || service?.id;
      const name = normalizeName(service?.service_name || service?.name);

      if (id && !servicePriorityById.has(String(id))) {
        servicePriorityById.set(String(id), priority);
      }

      if (name) {
        const existing = servicePriorityByName.get(name);
        if (typeof existing === "undefined" || sortPriority < getSortPriority(existing)) {
          servicePriorityByName.set(name, priority);
        }
      }
    });

    const resolveServicePriority = (service) => {
      const serviceId = service?._id || service?.id;
      if (serviceId && servicePriorityById.has(String(serviceId))) {
        return servicePriorityById.get(String(serviceId));
      }

      const serviceName = normalizeName(service?.name || service?.service_name);
      if (serviceName && servicePriorityByName.has(serviceName)) {
        return servicePriorityByName.get(serviceName);
      }

      const directPriority = service?.priority;
      if (getSortPriority(directPriority) !== Number.MAX_SAFE_INTEGER) {
        return directPriority;
      }

      return null;
    };

    const addLine = (section, description, count, amount, priority = null) => {
      const label = String(description || "").trim();
      const qty = Number(count) || 0;
      const total = Number(amount) || 0;
      if (!label || (!qty && !total)) return;

      const key = label.toLowerCase();
      const targetMap = sectionMaps[section] || sectionMaps.service;
      const existing = targetMap.get(key) || {
        description: label,
        count: 0,
        amountValue: 0,
        section,
        priority: null,
      };

      existing.count += qty;
      existing.amountValue += total;
      const nextPriority = getSortPriority(priority);
      const currentPriority = getSortPriority(existing.priority);
      if (nextPriority < currentPriority) {
        existing.priority = priority;
      }
      targetMap.set(key, existing);
    };

    receipts.forEach((receipt) => {
      (Array.isArray(receipt.services) ? receipt.services : []).forEach(
        (serviceItem) => {
          if (isIncludedGroupServicePart(serviceItem)) return;
          const service = serviceItem?.value ?? serviceItem ?? {};
          const description = service.name || service.service_name || "Service";
          const quantity = Math.max(1, Number(service.quantity) || 1);
          const amount =
            Number(service.total_price) || (Number(service.price) || 0) * quantity;
          const type = String(service.type || "").toLowerCase();
          const section =
            type === "part"
              ? "part"
              : type === "oil_service"
                ? "oil"
                : type === "tire_service"
                  ? "tire"
                  : "service";
          addLine(
            section,
            description,
            quantity,
            amount,
            resolveServicePriority(service),
          );
        },
      );

      (Array.isArray(receipt.tires) ? receipt.tires : []).forEach((tire) => {
        const description =
          [tire.condition, tire.brand, tire.size].filter(Boolean).join(" ") ||
          "Tire Service";
        const quantity = Math.max(1, Number(tire.quantity) || 1);
        const amount =
          Number(tire.total_price) || (Number(tire.price) || 0) * quantity;
        addLine("tire", description, quantity, amount);
      });

      const oilAmount =
        Number(receipt.oilPrice || 0) +
        Number(receipt.extraOilPrice || 0) +
        Number(receipt.oilFilterPrice || 0) +
        Number(receipt.fullServicePrice || 0);

      if (oilAmount > 0) {
        const oilDescription = receipt.oil?.name
          ? `Oil Service - ${receipt.oil.name}`
          : "Oil Service";
        addLine("oil", oilDescription, 1, oilAmount, receipt.oil?.priority);
      }
    });

    const sectionOrder = [
      ["service", "Services"],
      ["part", "Parts"],
      ["oil", "Oil"],
      ["tire", "Tires"],
    ];

    const rows = [];

    sectionOrder.forEach(([sectionKey, sectionLabel]) => {
      const items = Array.from(sectionMaps[sectionKey].values())
        .sort(
          (a, b) =>
            getSortPriority(a.priority) - getSortPriority(b.priority) ||
            b.amountValue - a.amountValue ||
            a.description.localeCompare(b.description),
        )
        .map((row, index) => ({
          id: `line-${sectionKey}-${index}`,
          description: row.description,
          count: Number.isInteger(row.count) ? row.count : row.count.toFixed(2),
          amountValue: row.amountValue,
          section: sectionKey,
          priority: row.priority,
        }));

      if (!items.length) return;

      rows.push({
        id: `section-${sectionKey}`,
        description: sectionLabel,
        count: "",
        amountValue: null,
        variant: "section",
        bold: true,
        isSection: true,
      });
      rows.push(...items);
    });

    return rows;
  }, [receipts, servicesCatalog]);

  const breadcrumbs = [
    {
      label: "Home",
      path: "/user/dashboard",
      icon: <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />,
    },
    { label: "Daily Report", path: null },
  ];

  const statCards = [
    {
      title: "Customers",
      value: customers.length,
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      color: "#667eea",
      bgColor: "rgba(102, 126, 234, 0.1)",
    },
    {
      title: "Vehicles",
      value: vehicles.length,
      icon: <DirectionsCarIcon sx={{ fontSize: 40 }} />,
      color: "#f093fb",
      bgColor: "rgba(240, 147, 251, 0.1)",
    },
    {
      title: "Receipts",
      value: receipts.length,
      icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
      color: "#4facfe",
      bgColor: "rgba(79, 172, 254, 0.1)",
    },
    {
      title: "Total Sales",
      value: `$${calcPrice(receipts)}`,
      icon: <AttachMoneyIcon sx={{ fontSize: 40 }} />,
      color: "#43e97b",
      bgColor: "rgba(67, 233, 123, 0.1)",
    },
  ];

  // Customer columns
  const customerColumns = [
    {
      field: "serial",
      headerName: "Sr. #",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "name",
      headerName: "Customer Name",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "phone",
      headerName: "Phone",
      width: 140,
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1,
      minWidth: 200,
    },
  ];

  // Vehicle columns
  const vehicleColumns = [
    {
      field: "serial",
      headerName: "Sr. #",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "year",
      headerName: "Year",
      width: 100,
    },
    {
      field: "make",
      headerName: "Make",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "model",
      headerName: "Model",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "vin_number",
      headerName: "VIN Number",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "customer",
      headerName: "Customer",
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row.customer?.name || "",
    },
  ];

  // Receipt columns
  const receiptColumns = [
    {
      field: "serial",
      headerName: "Sr. #",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "customer",
      headerName: "Customer",
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row.customer?.name || "",
    },
    {
      field: "vehicle",
      headerName: "Vehicle",
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) =>
        row.vehicle ? `${row.vehicle.make} ${row.vehicle.model}` : "",
    },
    {
      field: "totalPrice",
      headerName: "Total Price",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => `$${formatCurrency(value)}`,
    },
    {
      field: "paid",
      headerName: "Amount Paid",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => `$${formatCurrency(value)}`,
    },
    {
      field: "remaining",
      headerName: "Remaining",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => `$${formatCurrency(value)}`,
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === "Paid" ? "success" : "warning"}
          sx={{ fontWeight: 600 }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <IconButton
          component={Link}
          to={`/user/receipts/receipt-details/${params.row._id}`}
          color="success"
          size="small"
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  // Prepare rows
  const customerRows = customers.map((customer, index) => ({
    ...customer,
    id: customer._id,
    serial: index + 1,
  }));

  const vehicleRows = vehicles.map((vehicle, index) => ({
    ...vehicle,
    id: vehicle._id,
    serial: index + 1,
  }));

  const receiptRows = receipts.map((receipt, index) => ({
    ...receipt,
    id: receipt._id,
    serial: index + 1,
  }));

  // ── Financial Summary columns ──
  const financialColumns = [
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 260,
      renderCell: ({ row, value }) => (
        <span style={{ fontWeight: row.bold ? 700 : 400 }}>{value}</span>
      ),
    },
    {
      field: "count",
      headerName: "Count",
      width: 100,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: ({ row, value }) => (
        <span style={{ fontWeight: row.bold ? 700 : 400 }}>{value}</span>
      ),
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 150,
      align: "right",
      headerAlign: "right",
      sortable: false,
      renderCell: ({ row, value }) => (
        <span style={{ fontWeight: row.bold ? 700 : 400 }}>{value}</span>
      ),
    },
  ];

  // ── Financial Summary rows — variant drives getRowSx ──
  const financialRows = [
    ...lineSummaryRows.map((row) =>
      row.isSection
        ? {
            id: row.id,
            description: row.description,
            count: "",
            amount: "",
            variant: "section",
            bold: true,
            isSection: true,
          }
        : {
            id: row.id,
            description: row.description,
            count: row.count,
            amount: `$ ${formatCurrency(row.amountValue)}`,
            variant: "normal",
            bold: false,
          },
    ),
    {
      id: "dealers",
      variant: "highlight",
      bold: true,
      description: "Total Sales to Non-Taxable Customers",
      count: dealersCount,
      amount: `$ ${formatCurrency(dealersSale)}`,
    },
    {
      id: "discounts",
      variant: "normal",
      bold: true,
      description: "Total Discounts",
      count: discountCount,
      amount: `$ ${formatCurrency(totalDiscount)}`,
    },
    {
      id: "tax",
      variant: "normal",
      bold: true,
      description: "Total Tax",
      count: taxCount,
      amount: `$ ${formatCurrency(totalTax)}`,
    },
    {
      id: "gross",
      variant: "highlight",
      bold: true,
      description: "Gross Sales",
      count: "---",
      amount: `$ ${formatCurrency(netSale - totalDiscount - totalTax)}`,
    },
    {
      id: "net",
      variant: "total",
      bold: true,
      description: "Net Sales",
      count: "---",
      amount: `$ ${formatCurrency(netSale)}`,
    },
    {
      id: "netpaid",
      variant: "highlight",
      bold: false,
      description: "Net Sales (Paid Only)",
      count: "---",
      amount: `$ ${formatCurrency(paidToday)}`,
    },
    {
      id: "cash",
      variant: "normal",
      bold: false,
      description: "Total Paid w/ Cash",
      count: "---",
      amount: `$ ${formatCurrency(cashPayment)}`,
    },
    {
      id: "card",
      variant: "normal",
      bold: false,
      description: "Total Paid w/ Card",
      count: "---",
      amount: `$ ${formatCurrency(cardPayment)}`,
    },
    {
      id: "check",
      variant: "normal",
      bold: false,
      description: "Total Paid w/ Check",
      count: "---",
      amount: `$ ${formatCurrency(checkPayment)}`,
    },
    {
      id: "todayowed",
      variant: "warning",
      bold: true,
      description: "Today Total Owed (Fleet Accounts)",
      count: "---",
      amount: `$ ${formatCurrency(todayRemaining)}`,
    },
    {
      id: "totalowed",
      variant: "danger",
      bold: true,
      description: "Total Owed (Fleet Accounts)",
      count: "---",
      amount: `$ ${formatCurrency(totalRemaining)}`,
    },
  ];

  // ── Row background map ──
  const financialRowSx = (row) => {
    const map = {
      section: {
        backgroundColor: "#ffffff",
        "& .MuiDataGrid-cell": {
          fontWeight: 700,
          borderTop: "1px solid rgba(47, 85, 151, 0.24)",
        },
      },
      highlight: { backgroundColor: "#f8f9fa" },
      total: {
        backgroundColor: "#e3f2fd",
        borderTop: "2px solid #667eea",
        borderBottom: "2px solid #667eea",
      },
      warning: { backgroundColor: "#fff3cd" },
      danger: { backgroundColor: "#f8d7da" },
      normal: {},
    };
    return map[row.variant] || {};
  };

  return (
    <>
      <Box
        className="screen-only"
        sx={{
          pt: { xs: 10, sm: 11, md: 12 },
          pb: 3,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: activeTab === 0 ? "visible" : "hidden",
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            flex: activeTab === 0 ? "unset" : 1,
            display: "flex",
            flexDirection: "column",
            maxWidth: "100% !important",
            px: { xs: 2, sm: 3, md: 4 },
            overflow: activeTab === 0 ? "visible" : "hidden",
            minHeight: activeTab === 0 ? "auto" : 0,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              p: 3,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: 2,
              flexShrink: 0,
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                right: 0,
                width: "300px",
                height: "300px",
                background: "rgba(255, 255, 255, 0.05)",
                borderRadius: "50%",
                transform: "translate(30%, -30%)",
              },
            }}
          >
            <Breadcrumbs
              separator={
                <NavigateNextIcon
                  fontSize="small"
                  sx={{ color: "rgba(255,255,255,0.7)" }}
                />
              }
              sx={{ mb: 2 }}
            >
              {breadcrumbs.map((item, index) => {
                const isLast = index === breadcrumbs.length - 1;
                return isLast ? (
                  <Typography
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      color: "white",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                    }}
                  >
                    {item.icon}
                    {item.label}
                  </Typography>
                ) : (
                  <MuiLink
                    key={index}
                    component={Link}
                    to={item.path}
                    underline="hover"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      color: "rgba(255,255,255,0.9)",
                      fontSize: "0.875rem",
                      "&:hover": { color: "white" },
                    }}
                  >
                    {item.icon}
                    {item.label}
                  </MuiLink>
                );
              })}
            </Breadcrumbs>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                zIndex: 1,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    mb: 0.5,
                    textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  Daily Report
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.95rem" }}
                >
                  {selectedDateLabel}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  type="date"
                  size="small"
                  value={selectedDate}
                  onChange={handleDateChange}
                  inputProps={{ max: toLocalDateString(new Date()) }}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                    borderRadius: 1,
                    "& .MuiOutlinedInput-root": {
                      color: "white",
                      "& fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.3)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "white",
                      },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<PrintIcon />}
                  onClick={() => window.print()}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    color: "white",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    },
                  }}
                >
                  Print
                </Button>
              </Stack>
            </Box>
          </Paper>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            {statCards.map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1, fontWeight: 500 }}
                        >
                          {stat.title}
                        </Typography>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 700,
                            color: stat.color,
                          }}
                        >
                          {stat.value}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          on {selectedDateLabel}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: 70,
                          height: 70,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: stat.bgColor,
                          color: stat.color,
                        }}
                      >
                        {stat.icon}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Paper
            elevation={1}
            sx={{
              borderRadius: 2,
              flex: activeTab === 0 ? "unset" : 1,
              display: "flex",
              flexDirection: "column",
              overflow: activeTab === 0 ? "visible" : "hidden",
              minHeight: activeTab === 0 ? "auto" : 0,
              mb: 5.25,
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    minHeight: "56px",
                  },
                }}
              >
                <Tab
                  icon={<AssessmentIcon sx={{ fontSize: 20 }} />}
                  iconPosition="start"
                  label="Financial Summary"
                />
                <Tab
                  icon={<ReceiptIcon sx={{ fontSize: 20 }} />}
                  iconPosition="start"
                  label={`Receipts (${receipts.length})`}
                />
                <Tab
                  icon={<PeopleIcon sx={{ fontSize: 20 }} />}
                  iconPosition="start"
                  label={`Customers (${customers.length})`}
                />
                <Tab
                  icon={<DirectionsCarIcon sx={{ fontSize: 20 }} />}
                  iconPosition="start"
                  label={`Vehicles (${vehicles.length})`}
                />
              </Tabs>
            </Box>

            <Box
              sx={{
                flex: activeTab === 0 ? "unset" : 1,
                overflow: activeTab === 0 ? "visible" : "hidden",
                display: "flex",
                flexDirection: "column",
                minHeight: activeTab === 0 ? "auto" : 0,
              }}
            >
              {activeTab === 0 && (
                <ReusableDataGrid
                  rows={financialRows}
                  loading={loading}
                  columns={financialColumns}
                  noRowsMessage="No financial data for this date"
                  disableSorting
                  disablePagination
                  disableInternalScroll
                  getRowSx={financialRowSx}
                />
              )}

              {activeTab === 1 && (
                <ReusableDataGrid
                  rows={receiptRows}
                  loading={loading}
                  columns={receiptColumns}
                  pageSize={25}
                  pageSizeOptions={[10, 25, 50, 100]}
                  noRowsMessage="No receipts available for this date"
                  autoHeight={false}
                  sx={{
                    flex: 1,
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "rgba(102, 126, 234, 0.04)",
                    },
                  }}
                />
              )}

              {activeTab === 2 && (
                <ReusableDataGrid
                  rows={customerRows}
                  loading={loading}
                  columns={customerColumns}
                  pageSize={25}
                  pageSizeOptions={[10, 25, 50, 100]}
                  noRowsMessage="No new customers for this date"
                  autoHeight={false}
                  sx={{
                    flex: 1,
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "rgba(102, 126, 234, 0.04)",
                    },
                  }}
                />
              )}

              {activeTab === 3 && (
                <ReusableDataGrid
                  rows={vehicleRows}
                  loading={loading}
                  columns={vehicleColumns}
                  pageSize={25}
                  pageSizeOptions={[10, 25, 50, 100]}
                  noRowsMessage="No new vehicles for this date"
                  autoHeight={false}
                  sx={{
                    flex: 1,
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "rgba(102, 126, 234, 0.04)",
                    },
                  }}
                />
              )}
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Print Layout */}
      <div className="daily-report-print">
        <div className="report-wrap">
          <div className="report-header">
            <div className="company-info">
              {shopLogoSrc && (
                <img
                  src={shopLogoSrc}
                  alt="Company Logo"
                  className="company-logo-print"
                />
              )}
              {!shopLogoSrc && Helpers.authShop?.name && (
                <h2 className="company-name">{Helpers.authShop.name}</h2>
              )}
            </div>
            <div className="report-title">DAILY REPORT</div>
          </div>

          <div className="report-meta">
            <div>
              <strong>Report Date:</strong> {selectedDateLabel}
            </div>
            <div>
              <strong>Generated:</strong>{" "}
              {new Date().toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </div>
          </div>

          <div className="summary-stats">
            <div className="stat-box">
              <div className="stat-label">New Customers</div>
              <div className="stat-value">{customers.length}</div>
            </div>
            <div className="stat-box">
              <div className="stat-label">New Vehicles</div>
              <div className="stat-value">{vehicles.length}</div>
            </div>
            <div className="stat-box">
              <div className="stat-label">Receipts Generated</div>
              <div className="stat-value">{receipts.length}</div>
            </div>
            <div className="stat-box">
              <div className="stat-label">Total Sales</div>
              <div className="stat-value">${calcPrice(receipts)}</div>
            </div>
          </div>

          <div className="table-section">
            <div className="section-title-with-divider">
              <span className="section-title blue">Financial Summary</span>
            </div>
            <table className="report-table">
              <thead>
                <tr>
                  <th style={{ width: "50%" }}>Description</th>
                  <th style={{ textAlign: "center" }}>Count</th>
                  <th style={{ textAlign: "right" }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {financialRows.map((row) => {
                  const rowClassName =
                    row.variant === "section"
                      ? "section-row"
                      : row.variant === "highlight"
                      ? "highlight-row"
                      : row.variant === "total"
                        ? "total-row"
                        : row.variant === "warning"
                          ? "warning-row"
                          : row.variant === "danger"
                            ? "danger-row"
                            : "";

                  return (
                    <tr key={row.id} className={rowClassName}>
                      <td>{row.bold ? <strong>{row.description}</strong> : row.description}</td>
                      <td style={{ textAlign: "center" }}>
                        {row.bold ? <strong>{row.count}</strong> : row.count}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {row.bold ? <strong>{row.amount}</strong> : row.amount}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="report-footer">
            Generated by{" "}
            {Helpers.authShop?.name || "Auto Shop Management System"}
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>
        {`
          .daily-report-print { 
            display: none; 
          }

          @media print {
            html,
            body,
            #root,
            #layout-wrapper,
            .main-content,
            .page-content {
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              min-width: 100% !important;
              max-width: 100% !important;
              height: auto !important;
              min-height: 0 !important;
              max-height: none !important;
              overflow: visible !important;
              background: #fff !important;
            }

            body {
              margin: 0;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            .screen-only {
              display: none !important;
              height: 0 !important;
              overflow: hidden !important;
            }
            
            .daily-report-print {
              display: block !important;
              width: 100% !important;
              min-height: 100vh;
              padding: 0;
              margin: 0;
              background: #fff !important;
              color: #111827;
              font-family: Arial, sans-serif;
              overflow: visible !important;
            }
            
            @page {
              size: auto;
              margin: 0.45in;
            }

            .report-wrap {
              width: 100%;
              font-size: 11px;
              color: #000;
              line-height: 1.3;
              overflow: visible !important;
              break-inside: auto;
              page-break-inside: auto;
            }

            .report-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              border-bottom: 3px solid #2f5597;
              padding-bottom: 8px;
              margin-bottom: 12px;
            }

            .company-info {
              display: flex;
              flex-direction: column;
              align-items: flex-start;
            }

            .company-logo-print {
              max-height: 50px;
              max-width: 180px;
              object-fit: contain;
            }

            .company-name {
              margin: 0;
              font-size: 20px;
              font-weight: bold;
              color: #2f5597;
            }

            .report-title {
              font-size: 24px;
              font-weight: bold;
              color: #2f5597;
            }

            .report-meta {
              display: flex;
              justify-content: space-between;
              margin-bottom: 15px;
              font-size: 11px;
            }

            .summary-stats {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 10px;
              margin-bottom: 15px;
              padding: 10px;
              background-color: #f8f9fa;
              border-radius: 4px;
            }

            .stat-box {
              text-align: center;
              padding: 8px;
            }

            .stat-label {
              font-size: 10px;
              color: #666;
              text-transform: uppercase;
              margin-bottom: 4px;
            }

            .stat-value {
              font-size: 16px;
              font-weight: bold;
              color: #2f5597;
            }

            .section-title-with-divider {
              display: flex;
              align-items: center;
              margin-bottom: 10px;
              position: relative;
            }

            .section-title-with-divider::before,
            .section-title-with-divider::after {
              content: '';
              flex: 1;
              height: 2px;
              background: #2f5597;
            }

            .section-title-with-divider::before {
              margin-right: 15px;
            }

            .section-title-with-divider::after {
              margin-left: 15px;
            }

            .section-title.blue {
              background: #2f5597 !important;
              color: white !important;
              padding: 5px 15px;
              white-space: nowrap;
              font-size: 12px;
              font-weight: bold;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .table-section {
              margin-bottom: 15px;
              break-inside: auto;
              page-break-inside: auto;
            }

            .report-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 11px;
              break-inside: auto;
              page-break-inside: auto;
            }

            .report-table thead {
              background-color: #7a8181 !important;
              color: #000000 !important;
              font-weight: 600;
              text-transform: uppercase;
              border: 1px solid #000;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .report-table th {
              padding: 6px 8px;
              font-size: 10px;
              border: 1px solid #000;
            }

            .report-table td {
              padding: 5px 8px;
              border-bottom: 1px solid #ddd;
              vertical-align: top;
              line-height: 1.4;
            }

            .report-table .highlight-row {
              background-color: #f8f9fa !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .report-table .section-row td {
              font-weight: 700;
              border-top: 1px solid #9db3db;
              background-color: #ffffff !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .report-table .total-row {
              background-color: #e3f2fd !important;
              font-weight: bold;
              border-top: 2px solid #2f5597;
              border-bottom: 2px solid #2f5597;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .report-table .warning-row {
              background-color: #fff3cd !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .report-table .danger-row {
              background-color: #f8d7da !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }

            .report-footer {
              margin-top: 20px;
              text-align: center;
              font-weight: bold;
              color: #2f5597;
              font-size: 11px;
              border-top: 2px solid #2f5597;
              padding-top: 10px;
            }
          }
        `}
      </style>
    </>
  );
}

export default DailyReports;
